import { useId, useRef, useState, type ChangeEvent, type DragEvent } from 'react';

import type { ApiError } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PaperclipIcon, XIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import { formatBytes } from '@/shared/utils/format';

import { ACCEPTED_EXTENSIONS, problemWith, readingEstimateText } from './offerFiles';

const ACCEPT = ACCEPTED_EXTENSIONS.join(',');

/**
 * The supplier's document, chosen by hand and sent to be read (contract v8).
 *
 * A file input behind a drop zone: the input is the accessible control (label, keyboard,
 * screen reader), the zone is the same thing for a mouse. Nothing is sent until «Leer el
 * documento» is pressed, so choosing the wrong file costs one click and no request.
 */
export function OfferUpload({
  onUpload,
  pending,
  error,
}: {
  onUpload: (files: File[]) => void;
  pending: boolean;
  error: ApiError | null;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const problem = problemWith(files);

  function choose(list: FileList | null): void {
    setFiles(list ? Array.from(list) : []);
  }
  function onChange(event: ChangeEvent<HTMLInputElement>): void {
    choose(event.target.files);
  }
  function onDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setDragging(false);
    choose(event.dataTransfer.files);
  }
  function remove(index: number): void {
    setFiles((current) => current.filter((_, at) => at !== index));
    // The input still holds the old selection; clearing it lets the same file be chosen
    // again after being removed, which otherwise fires no `change`.
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'rounded-lg border border-dashed px-4 py-5 text-center transition-colors',
          dragging ? 'border-accent bg-accent/5' : 'border-line-2 bg-paper-2',
        )}
      >
        <PaperclipIcon size={22} className="text-ink-3 mx-auto" />
        <p className="text-ink mt-2 text-[14.5px]">
          <label htmlFor={inputId} className="link cursor-pointer font-medium">
            Elige el documento del proveedor
          </label>{' '}
          o arrástralo aquí.
        </p>
        <p className="text-ink-3 mt-1 text-[12.5px]">
          PDF, foto (JPG, PNG, HEIC), Excel o Word. Varios archivos, una sola oferta.
        </p>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={onChange}
          disabled={pending}
          className="sr-only"
        />
      </div>

      {files.length > 0 ? (
        <ul className="flex flex-col gap-1 text-[14px]">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="border-line-2 flex items-center justify-between gap-3 rounded-md border px-3 py-1.5"
            >
              <span className="text-ink min-w-0 truncate">
                {file.name}{' '}
                <span className="text-ink-3 text-[12.5px]">{formatBytes(file.size)}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Quitar ${file.name}`}
                onClick={() => remove(index)}
                disabled={pending}
                icon={<XIcon size={14} />}
              >
                Quitar
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {problem ? (
        <p role="alert" className="text-danger text-[14px]">
          {problem}
        </p>
      ) : files.length > 0 ? (
        // Said before the click, from the names alone: a photo is a minute of waiting,
        // and a person who knows that does not press the button twice.
        <p className="text-ink-3 text-[13px]">
          {readingEstimateText(files.map((file) => file.name))}
        </p>
      ) : null}
      {error ? <ErrorMessage error={error}>No se pudo subir el documento</ErrorMessage> : null}

      <div>
        <Button
          onClick={() => onUpload(files)}
          disabled={pending || files.length === 0 || problem !== null}
        >
          {pending ? 'Subiendo…' : 'Leer el documento'}
        </Button>
      </div>
    </div>
  );
}
